import React, { useState, useMemo } from 'react';
import { Search, Globe, ChevronRight, Filter, ShieldCheck, ArrowUpDown, ChevronLeft, Sparkles, Layers, SlidersHorizontal, PackageX, Home, Tag } from 'lucide-react';
import { Product, Currency, DocumentCategory, SoftwareFormat } from '../types';
import { ProductCard } from './ProductCard';
import { ProductSkeletonGrid } from './ProductSkeleton';
import { 
  CountryModel, 
  INITIAL_COUNTRIES, 
  searchCountries, 
  filterProductsByCountryModel, 
  computeCountryStatistics 
} from '../lib/countriesService';
import { slugify, getCountrySlug } from '../lib/slugUtils';
import { isProductPublished } from '../lib/statsService';

interface CountriesPageProps {
  darkMode: boolean;
  selectedCountry?: string | null;
  onSelectCountry?: (countryName: string | null) => void;
  products?: Product[];
  countriesList?: CountryModel[];
  currency?: Currency;
  wishlistItems?: Product[];
  onToggleWishlist?: (p: Product) => void;
  onAddToCart?: (p: Product) => void;
  onQuickView?: (p: Product) => void;
  onQuickBuy?: (p: Product) => void;
  isProductsLoading?: boolean;
  hasFirestoreLoaded?: boolean;
}

export const CountriesPage: React.FC<CountriesPageProps> = ({
  darkMode,
  selectedCountry,
  onSelectCountry,
  products = [],
  countriesList = INITIAL_COUNTRIES,
  currency = 'USD',
  wishlistItems = [],
  onToggleWishlist = () => {},
  onAddToCart = () => {},
  onQuickView = () => {},
  onQuickBuy = () => {},
  isProductsLoading = false,
  hasFirestoreLoaded = true,
}) => {
  // Master Country List (Firestore synced or initial fallback)
  const masterCountries = useMemo(() => {
    const list = countriesList && countriesList.length > 0 ? countriesList : INITIAL_COUNTRIES;
    return list.filter(c => c && typeof c === 'object');
  }, [countriesList]);

  // Search State for Country Search
  const [countrySearch, setCountrySearch] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<'All' | 'WithProducts' | 'WithoutProducts' | 'Featured'>('All');

  // Country Listing Page Filters & Sorting State
  const [categoryFilter, setCategoryFilter] = useState<DocumentCategory | 'All'>('All');
  const [priceFilter, setPriceFilter] = useState<'All' | 'Under20' | '20to50' | 'Over50'>('All');
  const [formatFilter, setFormatFilter] = useState<SoftwareFormat | 'All'>('All');
  const [inCountrySearch, setInCountrySearch] = useState('');
  const [sortBy, setSortBy] = useState<
    'newest' | 'oldest' | 'price-low' | 'price-high' | 'rating' | 'downloads' | 'trending' | 'bestselling'
  >('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleCount, setVisibleCount] = useState(20);
  const itemsPerPage = 12;

  // Active products filter (published / active)
  const publishedProducts = useMemo(() => {
    return (products || []).filter(p => p && isProductPublished(p));
  }, [products]);

  // Dynamic Country Statistics from live MySQL / Firestore
  const countryStats = useMemo(() => {
    return computeCountryStatistics(masterCountries, publishedProducts);
  }, [masterCountries, publishedProducts]);

  // Countries with Live Product Counts
  const countriesWithCounts = useMemo<(CountryModel & { productCount: number })[]>(() => {
    return masterCountries.map(c => {
      const matchingProds = filterProductsByCountryModel(publishedProducts, c);
      return {
        ...c,
        productCount: matchingProds?.length || 0
      };
    });
  }, [masterCountries, publishedProducts]);

  // Filtered Countries List based on Country Search Bar & Filter Pills
  const filteredCountries = useMemo(() => {
    let result = searchCountries(countriesWithCounts, countrySearch);

    if (selectedFilter === 'WithProducts') {
      result = result.filter(c => (c.productCount || 0) > 0);
    } else if (selectedFilter === 'WithoutProducts') {
      result = result.filter(c => (c.productCount || 0) === 0);
    } else if (selectedFilter === 'Featured') {
      result = result.filter(c => c.is_featured);
    }

    return result.sort((a, b) => (a.display_order || 999) - (b.display_order || 999));
  }, [countrySearch, selectedFilter, countriesWithCounts]);

  // Currently Selected Country Data
  const currentCountryObj = useMemo(() => {
    if (!selectedCountry) return null;
    const norm = (typeof selectedCountry === 'string' ? selectedCountry : '').toLowerCase().trim();
    return masterCountries.find(
      c => (c.name && (typeof c.name === 'string' ? c.name.toLowerCase() : '') === norm) || 
           (c.slug && c.slug === norm) || 
           (c.name && slugify(c.name) === norm) || 
           (c.iso_code && (typeof c.iso_code === 'string' ? c.iso_code.toLowerCase() : '') === norm)
    ) || null;
  }, [selectedCountry, masterCountries]);

  // Raw Products belonging to selected country
  const rawCountryProducts = useMemo(() => {
    if (!selectedCountry) return [];
    if (currentCountryObj) {
      return filterProductsByCountryModel(publishedProducts, currentCountryObj);
    }
    return filterProductsByCountryModel(publishedProducts, selectedCountry);
  }, [selectedCountry, currentCountryObj, publishedProducts]);

  // Filtered & Sorted Products for Selected Country Page
  const countryProductsFiltered = useMemo(() => {
    let list = [...rawCountryProducts];

    // Search query inside country products
    if (inCountrySearch.trim()) {
      const q = (typeof inCountrySearch === 'string' ? inCountrySearch : '').toLowerCase().trim();
      list = list.filter(p => p && (
        (typeof p.title === 'string' ? p.title.toLowerCase() : '').includes(q) ||
        (typeof p.description === 'string' ? p.description.toLowerCase() : '').includes(q) ||
        (typeof p.category === 'string' ? p.category.toLowerCase() : '').includes(q) ||
        (p.tags && Array.isArray(p.tags) && p.tags.some(t => (t && typeof t === 'string' ? t.toLowerCase() : '').includes(q))))
      );
    }

    // Category Filter
    if (categoryFilter !== 'All') {
      list = list.filter(p => p && p.category === categoryFilter);
    }

    // Price Filter
    if (priceFilter === 'Under20') {
      list = list.filter(p => p && (p.price || 0) < 20);
    } else if (priceFilter === '20to50') {
      list = list.filter(p => p && (p.price || 0) >= 20 && (p.price || 0) <= 50);
    } else if (priceFilter === 'Over50') {
      list = list.filter(p => p && (p.price || 0) > 50);
    }

    // Format Filter
    if (formatFilter !== 'All') {
      list = list.filter(p => p && p.formats && p.formats.includes(formatFilter));
    }

    // Sorting Logic
    switch (sortBy) {
      case 'newest':
        return list.sort((a, b) => {
          const tA1 = a.updatedAt ? new Date(a.updatedAt).getTime() : NaN;
          const tA2 = a.createdAt ? new Date(a.createdAt).getTime() : NaN;
          const tB1 = b.updatedAt ? new Date(b.updatedAt).getTime() : NaN;
          const tB2 = b.createdAt ? new Date(b.createdAt).getTime() : NaN;
          const vA = !isNaN(tA1) ? tA1 : (!isNaN(tA2) ? tA2 : 0);
          const vB = !isNaN(tB1) ? tB1 : (!isNaN(tB2) ? tB2 : 0);
          return vB - vA;
        });
      case 'oldest':
        return list.sort((a, b) => {
          const tA1 = a.updatedAt ? new Date(a.updatedAt).getTime() : NaN;
          const tA2 = a.createdAt ? new Date(a.createdAt).getTime() : NaN;
          const tB1 = b.updatedAt ? new Date(b.updatedAt).getTime() : NaN;
          const tB2 = b.createdAt ? new Date(b.createdAt).getTime() : NaN;
          const vA = !isNaN(tA1) ? tA1 : (!isNaN(tA2) ? tA2 : 0);
          const vB = !isNaN(tB1) ? tB1 : (!isNaN(tB2) ? tB2 : 0);
          return vA - vB;
        });
      case 'price-low':
        return list.sort((a, b) => (a.price || 0) - (b.price || 0));
      case 'price-high':
        return list.sort((a, b) => (b.price || 0) - (a.price || 0));
      case 'rating':
        return list.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      case 'downloads':
        return list.sort((a, b) => (b.salesCount || 0) - (a.salesCount || 0));
      case 'trending':
        return list.sort((a, b) => (b.isTrending ? 1 : 0) - (a.isTrending ? 1 : 0));
      case 'bestselling':
        return list.sort((a, b) => (b.isPopular ? 1 : 0) - (a.isPopular ? 1 : 0));
      default:
        return list;
    }
  }, [rawCountryProducts, inCountrySearch, categoryFilter, priceFilter, formatFilter, sortBy]);

  // Pagination for Country Product Grid
  const totalPages = Math.ceil((countryProductsFiltered?.length || 0) / itemsPerPage);
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return countryProductsFiltered.slice(start, start + itemsPerPage);
  }, [countryProductsFiltered, currentPage, itemsPerPage]);

  const handleCountryClick = (cName: string) => {
    if (onSelectCountry) {
      onSelectCountry(cName);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="py-10 relative z-10 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">

        {/* ========================================================= */}
        {/* VIEW A: DEDICATED COUNTRY LISTING PAGE (When Selected)      */}
        {/* ========================================================= */}
        {selectedCountry ? (
          <div className="space-y-8 animate-fadeIn">
            
            {/* Breadcrumb Navigation Header */}
            <div className="flex items-center justify-between text-xs font-semibold border-b border-slate-800/80 pb-4">
              <div className="flex items-center gap-2 text-slate-400 overflow-x-auto">
                <button 
                  onClick={() => onSelectCountry?.(null)}
                  className="flex items-center gap-1 hover:text-cyan-400 transition-colors"
                >
                  <Home className="w-3.5 h-3.5" />
                  <span>Home</span>
                </button>
                <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
                <button 
                  onClick={() => onSelectCountry?.(null)}
                  className="hover:text-cyan-400 transition-colors"
                >
                  Countries
                </button>
                <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
                <span className="text-cyan-400 font-bold">{currentCountryObj?.name || selectedCountry}</span>
              </div>

              <button
                onClick={() => onSelectCountry?.(null)}
                className="px-3.5 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-slate-300 hover:text-white hover:border-cyan-500/50 text-xs font-bold transition-all flex items-center gap-1.5"
              >
                <ChevronLeft className="w-4 h-4" />
                <span>All Countries</span>
              </button>
            </div>

            {/* Country Listing Hero Banner */}
            <div className={`p-6 sm:p-8 rounded-3xl border relative overflow-hidden ${
              darkMode ? 'glass-card border-white/10' : 'glass-card-light border-slate-200'
            }`}>
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10">
                <div className="flex items-start sm:items-center gap-5">
                  {/* Country Flag Thumbnail */}
                  <div className="relative w-20 h-14 sm:w-24 sm:h-16 rounded-2xl overflow-hidden border-2 border-white/20 shadow-2xl flex-shrink-0 bg-slate-900">
                    <img
                      src={(currentCountryObj?.flag && typeof currentCountryObj.flag === 'string' && currentCountryObj.flag.includes('http')) ? currentCountryObj.flag : `https://flagcdn.com/w160/${typeof currentCountryObj?.iso_code === 'string' ? currentCountryObj.iso_code.toLowerCase() : 'us'}.png`}
                      alt={`${selectedCountry} Flag`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLElement).style.display = 'none';
                      }}
                    />
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="px-2.5 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-[10px] font-black uppercase tracking-wider">
                        {typeof currentCountryObj?.iso_code === 'string' ? currentCountryObj.iso_code.toUpperCase() : 'ISO'} Document Vector Hub
                      </span>
                      <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-black">
                        {rawCountryProducts?.length || 0} {(rawCountryProducts?.length || 0) === 1 ? 'Product' : 'Products'}
                      </span>
                    </div>

                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight">
                      {currentCountryObj?.name || selectedCountry} Editable PSD Templates
                    </h1>

                    <p className={`text-xs sm:text-sm max-w-2xl leading-relaxed ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      {currentCountryObj?.description || `Explore high-resolution editable passport, ID card, driving license, utility bill, and bank statement PSD vector templates specifically designed for ${selectedCountry}.`}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 w-full md:w-auto">
                  <div className="p-4 rounded-2xl bg-slate-900/95 border border-slate-800 text-center w-full md:w-36">
                    <p className="text-[10px] font-extrabold uppercase text-slate-400">Total Products</p>
                    <p className="text-2xl font-black text-gradient">{rawCountryProducts?.length || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Filter Bar & Sorting Options */}
            <div className={`p-4 rounded-2xl border shadow-lg flex flex-col lg:flex-row items-center justify-between gap-4 ${
              darkMode ? 'bg-slate-900/95 border-slate-800' : 'bg-white border-slate-200'
            }`}>
              
              {/* Product Search inside Country */}
              <div className="relative w-full lg:w-64">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder={`Search ${selectedCountry} templates...`}
                  value={inCountrySearch}
                  onChange={(e) => { setInCountrySearch(e.target.value); setCurrentPage(1); }}
                  className={`w-full pl-10 pr-3 py-2 rounded-xl text-xs font-semibold border outline-none ${
                    darkMode ? 'bg-slate-950 border-slate-800 text-white placeholder-slate-500' : 'bg-slate-50 border-slate-300 text-slate-900'
                  }`}
                />
              </div>

              {/* Category Dropdown Filter */}
              <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
                <select
                  value={categoryFilter}
                  onChange={(e) => { setCategoryFilter(e.target.value as any); setCurrentPage(1); }}
                  className={`p-2 rounded-xl text-xs font-bold border outline-none ${
                    darkMode ? 'bg-slate-950 border-slate-800 text-slate-200' : 'bg-slate-50 border-slate-300 text-slate-800'
                  }`}
                >
                  <option value="All">All Categories</option>
                  <option value="Passport">Passport</option>
                  <option value="ID Card">ID Card</option>
                  <option value="Driving License">Driving License</option>
                  <option value="Certificate">Certificate</option>
                  <option value="Utility Bill">Utility Bill</option>
                  <option value="Bank Statement">Bank Statement</option>
                  <option value="Visa Documents">Visa Documents</option>
                  <option value="Resume">Resume</option>
                  <option value="Health ID">Health ID</option>
                  <option value="Templates">Templates</option>
                  <option value="Others">Others</option>
                </select>

                {/* Format Filter */}
                <select
                  value={formatFilter}
                  onChange={(e) => { setFormatFilter(e.target.value as any); setCurrentPage(1); }}
                  className={`p-2 rounded-xl text-xs font-bold border outline-none ${
                    darkMode ? 'bg-slate-950 border-slate-800 text-slate-200' : 'bg-slate-50 border-slate-300 text-slate-800'
                  }`}
                >
                  <option value="All">All Formats</option>
                  <option value="PSD">PSD Photoshop</option>
                  <option value="AI">AI Illustrator</option>
                  <option value="CDR">CDR CorelDRAW</option>
                </select>

                {/* Price Filter */}
                <select
                  value={priceFilter}
                  onChange={(e) => { setPriceFilter(e.target.value as any); setCurrentPage(1); }}
                  className={`p-2 rounded-xl text-xs font-bold border outline-none ${
                    darkMode ? 'bg-slate-950 border-slate-800 text-slate-200' : 'bg-slate-50 border-slate-300 text-slate-800'
                  }`}
                >
                  <option value="All">All Prices</option>
                  <option value="Under20">Under $20</option>
                  <option value="20to50">$20 - $50</option>
                  <option value="Over50">Over $50</option>
                </select>

                {/* Sort By Dropdown */}
                <div className="flex items-center gap-1.5 ml-auto lg:ml-0">
                  <ArrowUpDown className="w-3.5 h-3.5 text-cyan-400" />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className={`p-2 rounded-xl text-xs font-bold border outline-none ${
                      darkMode ? 'bg-slate-950 border-slate-800 text-cyan-400' : 'bg-slate-50 border-slate-300 text-slate-900'
                    }`}
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="price-low">Price: Low → High</option>
                    <option value="price-high">Price: High → Low</option>
                    <option value="rating">Highest Rated</option>
                    <option value="downloads">Most Downloaded</option>
                    <option value="trending">Trending</option>
                    <option value="bestselling">Best Selling</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Results Counter Summary */}
            <div className="flex items-center justify-between text-xs font-semibold text-slate-400 px-2">
              <span>Showing {countryProductsFiltered?.length || 0} products for {selectedCountry}</span>
              {(categoryFilter !== 'All' || priceFilter !== 'All' || formatFilter !== 'All' || inCountrySearch) && (
                <button
                  onClick={() => {
                    setCategoryFilter('All');
                    setPriceFilter('All');
                    setFormatFilter('All');
                    setInCountrySearch('');
                    setCurrentPage(1);
                  }}
                  className="text-cyan-400 hover:underline font-bold text-xs"
                >
                  Reset Filters
                </button>
              )}
            </div>

            {/* PRODUCT GRID FOR SELECTED COUNTRY */}
            {isProductsLoading ? (
              <ProductSkeletonGrid count={6} darkMode={darkMode} />
            ) : (countryProductsFiltered?.length || 0) === 0 ? (
                <div className="p-12 text-center rounded-3xl bg-slate-900/60 border border-slate-800 space-y-4 my-8">
                  <div className="w-16 h-16 rounded-2xl bg-slate-800/80 border border-slate-700 flex items-center justify-center mx-auto text-slate-400">
                    <PackageX className="w-8 h-8 text-amber-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">No products are currently available for this country.</h3>
                  <p className="text-xs text-slate-400 max-w-md mx-auto">
                    We are constantly expanding our collection. No matching templates were found for {selectedCountry} with the selected filters.
                  </p>
                  <div className="flex items-center justify-center gap-3 pt-2">
                    <button
                      onClick={() => {
                        setCategoryFilter('All');
                        setPriceFilter('All');
                        setFormatFilter('All');
                        setInCountrySearch('');
                      }}
                      className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-bold text-white border border-slate-700 transition-colors"
                    >
                      Clear Filter Criteria
                    </button>
                    <button
                      onClick={() => onSelectCountry?.(null)}
                      className="px-4 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 text-xs font-bold text-white shadow-lg"
                    >
                      Browse Other Countries
                    </button>
                  </div>
                </div>
            ) : (
              <div className="space-y-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {(paginatedProducts || []).map((p, idx) => (
                    <ProductCard
                      key={`${p.id}-${idx}`}
                      product={p}
                      currency={currency}
                      isInWishlist={(wishlistItems || []).some(i => i && i.id === p.id)}
                      onToggleWishlist={onToggleWishlist}
                      onAddToCart={onAddToCart}
                      onQuickView={onQuickView}
                      onQuickBuy={onQuickBuy}
                      darkMode={darkMode}
                    />
                  ))}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-6">
                    <button
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      className="p-2 rounded-xl bg-slate-900 border border-slate-800 text-slate-300 disabled:opacity-40 font-bold text-xs"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-9 h-9 rounded-xl text-xs font-extrabold border transition-all ${
                          currentPage === page
                            ? 'bg-cyan-600 text-white border-cyan-400 shadow-md shadow-cyan-500/20'
                            : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-800'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      className="p-2 rounded-xl bg-slate-900 border border-slate-800 text-slate-300 disabled:opacity-40 font-bold text-xs"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )}

          </div>
        ) : (

          /* ========================================================= */
          /* VIEW B: ALL COUNTRIES OVERVIEW & SMART COUNTRY SEARCH      */
          /* ========================================================= */
          <div className="space-y-10 animate-fadeIn">
            
            {/* Header Title Banner */}
            <div className="text-center space-y-4 max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-extrabold uppercase tracking-widest shadow-sm">
                <Globe className="w-3.5 h-3.5" />
                <span>Enterprise Country Directory</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white">
                Find templates by <span className="text-gradient">Country</span>
              </h1>

              <p className={`text-sm sm:text-base leading-relaxed ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                Select a country to instantly view its exclusive passport, ID card, driving license, and bank statement PSD vector templates.
              </p>
            </div>

            {/* Dynamic MySQL / Firestore Statistics Counter Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className={`p-4 rounded-2xl border text-center ${darkMode ? 'bg-slate-900/95 border-slate-800' : 'bg-white border-slate-200'}`}>
                <p className="text-[10px] font-extrabold uppercase text-slate-400">All Countries</p>
                <p className="text-2xl font-black text-cyan-400">{countryStats.totalCountries}</p>
              </div>

              <div className={`p-4 rounded-2xl border text-center ${darkMode ? 'bg-slate-900/95 border-slate-800' : 'bg-white border-slate-200'}`}>
                <p className="text-[10px] font-extrabold uppercase text-slate-400">With Templates</p>
                <p className="text-2xl font-black text-emerald-400">{countryStats.countriesWithProducts}</p>
              </div>

              <div className={`p-4 rounded-2xl border text-center ${darkMode ? 'bg-slate-900/95 border-slate-800' : 'bg-white border-slate-200'}`}>
                <p className="text-[10px] font-extrabold uppercase text-slate-400">Without Templates</p>
                <p className="text-2xl font-black text-slate-400">{countryStats.countriesWithoutProducts}</p>
              </div>

              <div className={`p-4 rounded-2xl border text-center ${darkMode ? 'bg-slate-900/95 border-slate-800' : 'bg-white border-slate-200'}`}>
                <p className="text-[10px] font-extrabold uppercase text-purple-400">Featured Regions</p>
                <p className="text-2xl font-black text-purple-400">{countryStats.featuredCountries.length}</p>
              </div>
            </div>

            {/* Search Box & Quick Filters */}
            <div className={`p-4 sm:p-6 rounded-3xl border shadow-2xl flex flex-col md:flex-row items-center justify-between gap-4 ${
              darkMode ? 'glass-card border-white/10' : 'glass-card-light border-slate-200'
            }`}>
              {/* Country Search Bar */}
              <div className="relative w-full md:w-96">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-400" />
                <input
                  type="text"
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  placeholder="Search countries (e.g. Canada, US, Germany, Ban...)"
                  className={`w-full pl-11 pr-10 py-3 rounded-2xl border text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-cyan-500/50 ${
                    darkMode
                      ? 'bg-slate-900/90 border-slate-700 text-white placeholder-slate-500'
                      : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400'
                  }`}
                />
                {countrySearch && (
                  <button
                    onClick={() => setCountrySearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 hover:text-white px-2 py-1 rounded-lg bg-slate-800"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Filter Pills */}
              <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto pb-1 md:pb-0">
                <button
                  onClick={() => setSelectedFilter('All')}
                  className={`px-4 py-2 rounded-xl text-xs font-bold border transition-all whitespace-nowrap ${
                    selectedFilter === 'All'
                      ? 'bg-blue-600 text-white border-blue-400 shadow-md shadow-blue-500/20'
                      : darkMode
                      ? 'bg-slate-900/60 border-slate-800 text-slate-300 hover:bg-slate-800'
                      : 'bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  All Countries ({countryStats.totalCountries})
                </button>

                <button
                  onClick={() => setSelectedFilter('WithProducts')}
                  className={`px-4 py-2 rounded-xl text-xs font-bold border transition-all whitespace-nowrap ${
                    selectedFilter === 'WithProducts'
                      ? 'bg-emerald-600 text-white border-emerald-400 shadow-md shadow-emerald-500/20'
                      : darkMode
                      ? 'bg-slate-900/60 border-slate-800 text-slate-300 hover:bg-slate-800'
                      : 'bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  Has Templates ({countryStats.countriesWithProducts})
                </button>

                <button
                  onClick={() => setSelectedFilter('Featured')}
                  className={`px-4 py-2 rounded-xl text-xs font-bold border transition-all whitespace-nowrap ${
                    selectedFilter === 'Featured'
                      ? 'bg-purple-600 text-white border-purple-400 shadow-md shadow-purple-500/20'
                      : darkMode
                      ? 'bg-slate-900/60 border-slate-800 text-slate-300 hover:bg-slate-800'
                      : 'bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  Featured Regions ({countryStats.featuredCountries.length})
                </button>
              </div>
            </div>

            {/* Counter bar */}
            <div className="flex items-center justify-between text-xs font-semibold px-2 text-slate-400">
              <span>Showing {filteredCountries.length} of {masterCountries.length} countries</span>
            </div>

            {/* Countries Cards Grid */}
            {filteredCountries.length === 0 ? (
              <div className="text-center py-20 space-y-4">
                <div className="w-16 h-16 rounded-3xl bg-slate-800/60 border border-slate-700 flex items-center justify-center mx-auto text-slate-400">
                  <Globe className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="font-extrabold text-xl text-white">No Countries Matched</h3>
                <p className="text-xs text-slate-400">No country matched your search query "{countrySearch}".</p>
                <button
                  onClick={() => {
                    setCountrySearch('');
                    setSelectedFilter('All');
                  }}
                  className="px-4 py-2 rounded-xl bg-cyan-600 text-white font-bold text-xs shadow-lg"
                >
                  Reset Search Filter
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-5">
                {filteredCountries.slice(0, visibleCount).map((country) => {
                  const flagUrl = (country.flag && typeof country.flag === 'string' && country.flag.includes('http')) ? country.flag : (country.iso_code ? `https://flagcdn.com/w160/${typeof country.iso_code === 'string' ? country.iso_code.toLowerCase() : ''}.png` : '');

                  return (
                    <div
                      key={country.id}
                      onClick={() => handleCountryClick(country.name)}
                      className={`group relative p-3 sm:p-4 rounded-2xl border transition-all duration-300 cursor-pointer flex items-center gap-3 sm:gap-4 md:hover:-translate-y-1 ${
                        darkMode
                          ? 'bg-slate-900/60 border-slate-800/80 hover:border-cyan-500/50 hover:bg-slate-800/80 hover:shadow-xl hover:shadow-cyan-500/10'
                          : 'bg-white border-slate-200/80 hover:border-blue-400 hover:shadow-lg hover:shadow-blue-500/10'
                      }`}
                    >
                      {/* Flag Thumbnail Container */}
                      <div className="relative w-10 h-7 sm:w-14 sm:h-10 rounded-md sm:rounded-lg overflow-hidden flex-shrink-0 border border-black/10 shadow-sm bg-slate-800 group-hover:scale-105 transition-transform duration-300">
                        <img
                          src={flagUrl}
                          alt={`${country.name} Flag`}
                          loading="lazy"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLElement).style.display = 'none';
                          }}
                        />
                      </div>

                      {/* Country Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold sm:font-extrabold text-xs sm:text-base tracking-tight truncate group-hover:text-cyan-400 transition-colors text-white">
                          {country.name}
                        </h4>
                        <p className="text-[10px] sm:text-xs font-medium opacity-60 flex items-center gap-1 sm:gap-1.5 mt-0.5">
                          <span className={`inline-block w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${
                            country.productCount > 0 ? 'bg-emerald-400 shadow-sm shadow-emerald-400/50' : 'bg-slate-500'
                          }`} />
                          <span className="truncate">{country.productCount} {country.productCount === 1 ? 'product' : 'products'}</span>
                        </p>
                      </div>

                      <ChevronRight className="w-4 h-4 text-slate-500 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all flex-shrink-0" />
                    </div>
                  );
                })}
              </div>
              
              {/* Load More Button */}
              {filteredCountries.length > visibleCount && (
                <div className="flex justify-center mt-10">
                  <button
                    onClick={() => setVisibleCount(prev => prev + 20)}
                    className={`px-8 py-3 rounded-xl font-bold text-sm transition-all shadow-lg ${
                      darkMode 
                        ? 'bg-slate-800 text-cyan-400 hover:bg-slate-700 hover:shadow-cyan-500/10' 
                        : 'bg-white text-blue-600 border border-blue-200 hover:bg-blue-50 hover:shadow-blue-500/10'
                    }`}
                  >
                    Load More Countries ({filteredCountries.length - visibleCount} remaining)
                  </button>
                </div>
              )}
              </>
            )}

          </div>
        )}

      </div>
    </div>
  );
};
